import React, { useEffect, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/models/dom";
import "tinymce/plugins/lists";
import "tinymce/plugins/link";
import "tinymce/plugins/wordcount";
import "tinymce/plugins/advlist";
import "tinymce/plugins/autolink";
import "tinymce/plugins/charmap";
import "tinymce/plugins/preview";
import "tinymce/plugins/anchor";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/code";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/insertdatetime";
import "tinymce/plugins/media";
import "tinymce/plugins/table";
import "tinymce/plugins/image";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/skins/content/default/content.min.css";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableImage = ({ id, url, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    display: "inline-block",
    cursor: "grab",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <img
        src={url}
        alt={`preview-${index}`}
        style={{
          width: "120px",
          height: "90px",
          objectFit: "cover",
          borderRadius: "8px",
          display: "block",
        }}
      />
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => onRemove(index)}
        style={{
          position: "absolute",
          top: "4px",
          right: "4px",
          background: "red",
          color: "white",
          border: "none",
          borderRadius: "50%",
          width: "20px",
          height: "20px",
          fontSize: "12px",
          cursor: "pointer",
          lineHeight: "1",
        }}
      >
        ✕
      </button>
      {index === 0 && (
        <span
          style={{
            position: "absolute",
            bottom: "4px",
            left: "4px",
            background: "#6565ff",
            color: "white",
            fontSize: "10px",
            padding: "1px 5px",
            borderRadius: "4px",
          }}
        >
          Cover
        </span>
      )}
    </div>
  );
};

const PortfolioForm = ({ initial = null, onSubmit, onUpload }) => {
  const [title, setTitle] = useState(initial?.title || "");
  const [websiteUrl, setWebsiteUrl] = useState(initial?.websiteUrl || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [images, setImages] = useState(
    (initial?.images || []).map((img, i) => ({
      ...img,
      id: img.id || img._id || `img-${i}-${Date.now()}`,
    })),
  );
  const [uploading, setUploading] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    if (initial) {
      setTitle(initial.title || "");
      setWebsiteUrl(initial.websiteUrl || "");
      setDescription(initial.description || "");
      setImages(
        (initial.images || []).map((img, i) => ({
          ...img,
          id: img.id || img._id || `img-${i}-${Date.now()}`,
        })),
      );
    }
  }, [initial]);

  const handleAddImage = async (file) => {
    if (!file) return;
    setUploading(true);
    const url = await onUpload(file);
    if (url) setImages((prev) => [...prev, { url, id: Date.now().toString() }]);
    setUploading(false);
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setImages((prev) => {
        const oldIndex = prev.findIndex((img) => img.id === active.id);
        const newIndex = prev.findIndex((img) => img.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({ title, description, images, websiteUrl });
  };

  return (
    <form onSubmit={submit}>
      <div className="mb-3">
        <label className="form-label text-light">Title</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="form-control rounded-4 p-3"
          placeholder="Project Title"
        />
      </div>

      <div className="mb-3">
        <label className="form-label text-light">Website URL</label>
        <input
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          className="form-control rounded-4 p-3"
          placeholder="https://example.com"
        />
      </div>

      <div className="mb-3">
        <label className="form-label text-light">Images</label>
        <input
          type="file"
          accept="image/*"
          className="form-control mb-2"
          disabled={uploading}
          onChange={(e) => handleAddImage(e.target.files?.[0])}
        />
        {uploading && (
          <div className="d-flex justify-content-center mt-2">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Uploading...</span>
            </div>
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={images.map((img, i) => img.id || i.toString())}
            strategy={horizontalListSortingStrategy}
          >
            <div className="d-flex flex-wrap gap-2 mt-2">
              {images.map((img, idx) => (
                <SortableImage
                  key={img.id || idx}
                  id={img.id || idx.toString()}
                  url={img.url}
                  index={idx}
                  onRemove={handleRemoveImage}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <small className="text-secondary">
          First image is the cover. Drag to reorder.
        </small>
      </div>

      <div className="mb-3">
        <label className="form-label text-light">Description</label>
        <Editor
          value={description}
          onEditorChange={(v) => setDescription(v)}
          init={{
            height: 500,
            menubar: "file edit view insert format tools table",
            toolbar_mode: "wrap",
            plugins:
              "advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table wordcount",
            toolbar:
              "undo redo | formatselect | bold italic underline forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media table | code fullscreen | removeformat",
            license_key: "gpl",
            branding: false,
            promotion: false,
          }}
        />
      </div>

      <div className="py-3 d-flex justify-content-center">
        <button disabled={uploading} className="btn btn-primary rounded-4 p-3">
          Save
        </button>
      </div>
    </form>
  );
};

export default PortfolioForm;
